/**
 * Optional industry templates. They only pre-fill configuration (form fields
 * and how the "inquiry subject" is labelled). The core model is identical for
 * every industry.
 */

export const FIELD_TYPES = ['text', 'textarea', 'number', 'select', 'email', 'phone', 'url', 'checkbox'] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export interface FormFieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  options?: string[];
  /** Hidden from viewers and masked in exports for lower roles. */
  sensitive?: boolean;
}

export interface IndustryTemplate {
  id: string;
  label: string;
  /** How the inquiry subject (InquiryContext) is described in the UI. */
  contextLabel: string;
  fields: FormFieldDefinition[];
  guidance?: string;
}

export const INDUSTRY_TEMPLATES: IndustryTemplate[] = [
  { id: 'general', label: 'General', contextLabel: 'Subject', fields: [] },
  {
    id: 'real_estate',
    label: 'Real estate',
    contextLabel: 'Property',
    fields: [
      { key: 'location', label: 'Preferred location', type: 'text', required: false },
      { key: 'budget', label: 'Budget', type: 'text', required: false },
      {
        key: 'property_type',
        label: 'Property type',
        type: 'select',
        required: false,
        options: ['Apartment', 'House', 'Land', 'Commercial', 'Other'],
      },
    ],
  },
  {
    id: 'automotive',
    label: 'Automotive',
    contextLabel: 'Vehicle',
    fields: [
      { key: 'model', label: 'Model', type: 'text', required: false },
      { key: 'budget', label: 'Budget', type: 'text', required: false },
      {
        key: 'purchase_intent',
        label: 'Purchase timeframe',
        type: 'select',
        required: false,
        options: ['This week', 'This month', 'In 3 months', 'Just browsing'],
      },
    ],
  },
  {
    id: 'retail',
    label: 'Retail & e-commerce',
    contextLabel: 'Product',
    fields: [
      { key: 'sku', label: 'Product or SKU', type: 'text', required: false },
      { key: 'order_reference', label: 'Order reference', type: 'text', required: false },
    ],
  },
  {
    id: 'healthcare',
    label: 'Healthcare',
    contextLabel: 'Service',
    fields: [
      {
        key: 'inquiry_category',
        label: 'Inquiry type',
        type: 'select',
        required: false,
        options: ['Book an appointment', 'Change an appointment', 'Pricing', 'General question'],
      },
    ],
    guidance:
      'Do not ask for symptoms, diagnoses or other health information in contact forms. Keep inquiries to scheduling and general questions.',
  },
  {
    id: 'education',
    label: 'Education',
    contextLabel: 'Program',
    fields: [
      { key: 'program', label: 'Course or program', type: 'text', required: false },
      {
        key: 'intake',
        label: 'Intended start',
        type: 'select',
        required: false,
        options: ['Next intake', 'Within 6 months', 'Next year', 'Not sure'],
      },
    ],
  },
  {
    id: 'hospitality',
    label: 'Hospitality',
    contextLabel: 'Booking',
    fields: [
      { key: 'dates', label: 'Dates', type: 'text', required: false },
      { key: 'guests', label: 'Number of guests', type: 'number', required: false },
    ],
  },
  {
    id: 'professional_services',
    label: 'Professional services',
    contextLabel: 'Service',
    fields: [{ key: 'company', label: 'Company', type: 'text', required: false }],
  },
];

export function getTemplate(id: string | null | undefined): IndustryTemplate {
  return INDUSTRY_TEMPLATES.find((t) => t.id === id) ?? INDUSTRY_TEMPLATES[0]!;
}
